<template>
<div class="card">
    <div v-if="isSimpleChart">
        <chart-container>
            <i slot="chart-header">{{ t(chartTitle)}}</i>
            <div slot="chart-body">
                <div>
                    <loading-indicator v-if="loading" />
                    <div style="position: relative;">
                        <component v-if="!loading" :is="simpleChartComponent" :chart-data="chartData" :options="chartOptions" />
                    </div>
                </div>
            </div>
        </chart-container>
    </div>
    <div v-if="isComplexChart && apollo">
        <component :is="complexChartComponent" :apollo="apollo" :videoApiKey="videoApiKey" :daysAgo="daysAgo" :refresh="refresh" :noRefresh="noRefresh" />
    </div>
</div>
</template>

<script>
import {
    get,
    map
} from 'lodash';
import ChartContainer from './charts/chartContainer.vue';
import LoadingIndicator from './loadingIndicator.vue';
import AdvancedInsights from '../models/advancedInsights.js';

import VueApollo from "vue-apollo";
import ApolloClient from 'apollo-client';
import {
    HttpLink
} from 'apollo-link-http';
import {
    setContext
} from 'apollo-link-context';
import {
    InMemoryCache
} from 'apollo-cache-inmemory';

export default {
    name: 'AdvancedInsightsChart',
    components: {
        ChartContainer,
        LoadingIndicator
    },
    props: {
        token: {
            type: String,
            required: true
        },
        videoApiKey: {
            type: String,
            required: true
        },
        chartType: {
            type: String,
            required: true
        },
        groupBy: {
            type: String,
            required: true
        },
        daysAgo: {
            type: Number,
            required: false
        },
        refresh: {
            type: Number,
            required: false,
            default: 5
        },
        noRefresh: {
            type: Boolean,
            required: false
        },
        baseLang: {
            type: Object,
            required: false
        },
        lang: {
            type: Object,
            required: false
        }

    },
    data() {
        return {
            advancedInsights: new AdvancedInsights(this.videoApiKey, this.daysAgo),
            chartTitle: '',
            chartData: {},
            chartOptions: {},
            chartComponent: '',
            loading: true,
            apollo: null,
            timer: null
        };
    },
    watch: {
        lang: function (newVal) {
            // Any strings that need to be dynamically changed on Language Change go here...
        }
    },
    async mounted() {
        this.loading = true;
        this.initApollo();
        await this.loadChart();

        if (this.isSimpleChart && !this.noRefresh) {
            this.timer = setInterval(async () => {
                this.advancedInsights = new AdvancedInsights(this.videoApiKey, this.daysAgo);
                await this.loadChart();
            }, (this.refresh <= 0 ? 5 : this.refresh) * 1000);
        }

    },
    beforeDestroy() {
        console.log("beforeDestroy for advancedInsights")
        if (this.timer) {
            clearInterval(this.timer)
        }
    },
    computed: {
        simpleChartComponent() {
            if (this.chartComponent) {
                return () => import(`./charts/${this.chartComponent}`);
            } else {
                return () => import(`./charts/errorChart`);
            }
        },
        complexChartComponent() {
            const chartComponent = this.advancedInsights.getComplexChartComponent(this.chartType, this.groupBy);

            if (chartComponent === 'errorChart') {
                return () => import(`./charts/errorChart`);
            }

            return () => import(`./charts/${chartComponent}`);
        },
        isSimpleChart() {
            return this.advancedInsights && this.advancedInsights.isSimpleChart(this.chartType, this.groupBy);
        },
        isComplexChart() {
            return this.advancedInsights && this.advancedInsights.isComplexChart(this.chartType, this.groupBy);
        },
    },
    methods: {
        initApollo() {
            const authMiddleware = setContext(() => ({
                headers: {
                    'X-OPENTOK-AUTH': this.token
                }
            }));

            const client = new ApolloClient({
                link: authMiddleware.concat(new HttpLink({
                    uri: 'https://insights.opentok.com/graphql'
                })),
                cache: new InMemoryCache()
            });

            this.apollo = new VueApollo({
                defaultClient: client
            });
        },
        async loadChart() {
            if (this.advancedInsights.isSimpleChart(this.chartType, this.groupBy)) {

                const query = this.advancedInsights.getSimpleQuery(this.chartType, this.groupBy);
                const data = await this.apollo.defaultClient.query({
                    query
                });
                const resources = this.advancedInsights.getProjectDataResourcesFromData(data);
                const chartDataObject = this.advancedInsights.getSimpleChartData(this.chartType, this.groupBy, resources);
                this.chartTitle = chartDataObject.title;
                this.chartData = chartDataObject.data;
                this.chartComponent = chartDataObject.component;
                this.chartOptions = chartDataObject.options;

            } else if (!this.advancedInsights.isComplexChart(this.chartType, this.groupBy)) {

                throw new Error(`${this.chartType} and ${this.groupBy} is not available! You may have to implement this.\nThe following charts are available:\n${this.advancedInsights.getAvailableCharts()}`);

            }

            this.loading = false;
        },
        t(key) { // T for Translate...
            return this.$tr(key, this.lang, this.baseLang)
        },
    },
}
</script>

<style scoped>
.card {
    display: inline-block;
    min-width: 300px;
    width: calc(100% - 24px);
    margin: 0 10px 20px 10px;
}
</style>
